/// <reference types="@angular/localize" />

import { enableProdMode } from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';
import { environment } from './environments/environment';
import { AppModule } from './app/app.module';
import { overrideConsoleLogs } from './app/shared/services/console-override.service';
import { bootstrapApplication } from '@angular/platform-browser';
import * as Sentry from "@sentry/angular";
import { appConfig } from './app/app.config';
import { AppComponent } from './app/app.component';

if (environment.production) {
  enableProdMode();
}

overrideConsoleLogs();

const company = localStorage.getItem('currentCompany');

const sentryDsns: Record<string, string> = {
  'unity': 'https://d5d17974b8b72086cdab440650796580@o4508377603506176.ingest.us.sentry.io/4509039961899008',
  'mingle': 'https://608333b9b245f69356efe7a7c66ffcf0@o4508377603506176.ingest.us.sentry.io/4509040080584704',
};

const selectedDsn = sentryDsns[company || 'unity'];
const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

Sentry.init({
  // ========================
  // 1. CORE SETTINGS
  // ========================
  dsn: selectedDsn, // Like a 'phone number' for Sentry to know where to send error reports
  release: `bss-app-dashboard@${environment.version}`, // "App name + version" to track which version caused errors
  environment: environment.production ? 'production' : 'development', // Tags errors as 'production' or 'development'

  // ========================
  // 2. FEATURES (INTEGRATIONS)
  // ========================
  integrations: [
    Sentry.browserTracingIntegration(), // Records how fast pages load (like a stopwatch)
    Sentry.replayIntegration(), // Records user screens (like a camera) to see what they did before errors
    Sentry.breadcrumbsIntegration() // Tracks clicks and navigation (like breadcrumbs in a forest)
  ],

  // ========================
  // 3. PERFORMANCE MONITORING
  // ========================
  /**
   * tracesSampleRate: How often to measure speed
   * - 0.5 = 50% of users get measured
   * - Lower numbers reduce server costs
   */
  tracesSampleRate: 0.5,

  /**
   * tracePropagationTargets: Which API calls to track
   * - Tracks requests to your server ('localhost') 
   * - Ignores other websites
   */
  tracePropagationTargets: ["localhost", /^https:\/\/yourserver\.io\/api/],

  profilesSampleRate: 0.1,
  // ========================
  // 4. SESSION REPLAY
  // ========================
  /**
   * replaysSessionSampleRate: Random screen recordings
   * - 0.1 = 10% of users get recorded
   * - Helps improve UX design
   */
  replaysSessionSampleRate: 0.05,

  /**
   * replaysOnErrorSampleRate: Record when errors happen
   * - 0.7 = 70% of error sessions recorded
   * - Shows exactly what went wrong
   */
  replaysOnErrorSampleRate: 0.7,

  // ========================
  // 5. ERROR HANDLING
  // ========================
  /**
   * beforeSend: Error filter (like a spam filter)
   * - Blocks known unimportant errors
   * - Keeps serious errors for investigation
   */
  beforeSend(event) {
    if (event.exception?.values?.[0]?.type === "HttpErrorResponse") {
      const statusCode = event.exception.values[0].value.match(/status code: (\d+)/)?.[1];
      // Add critical endpoint check
      const url = event.request?.url || '';
      if (statusCode && ["401", "403", "404"].includes(statusCode)){
        return null;
      }
    }
    const ignoredErrors = [
      // Browser/network issues
      "HttpErrorResponse", // API errors (400, 500 status codes)
      "NetworkError", // No internet connection
      "TimeoutError", // Slow API responses
      "ResizeObserver", // Browser animation quirk
      
      // Angular-specific
      "ExpressionChangedAfterItHasBeenCheckedError", // Development-mode warning
      
      // Third-party libraries
      /google/i // Google API errors
    ];
  
    const errorType = event.exception?.values?.[0]?.type || '';
    
    if (ignoredErrors.some(pattern => 
      typeof pattern === 'string' 
        ? pattern === errorType 
        : pattern.test(errorType)
    )) { 
      return null; // Block these errors
    }
    return event; // Send other errors
  },

  // ========================
  // 6. ADVANCED SETTINGS
  // ========================
  /**
   * transport: How to send data to Sentry
   * - Uses modern fetch() API (better than old XMLHttpRequest)
   */
  transport: (options) => Sentry.makeFetchTransport(options),

  /**
   * enabled: Master switch
   * - true = Sentry is ON
   * - Set to environment.production to disable in development
   */
  enabled: environment.production && !isLocalhost,

  // ========================
  // 7. RECOMMENDED ADDITIONS
  // ========================
  /**
   * attachStacktrace: Include detective clues
   * - Shows exact file and line number where error occurred
   */
  attachStacktrace: true,

  /**
   * ignoreErrors: Global error blacklist
   * - Blocks errors even before beforeSend runs
   */
  ignoreErrors: [
    // ========================
    // 1. BROWSER/CHUNK ERRORS
    // ========================
    "ChunkLoadError", // Failed lazy-loaded file downloads (common during deployments)
    /Loading chunk [\d]+ failed/, // Webpack chunk loading failures
    /Failed to fetch dynamically imported module/, // Network-related chunk failures

    // ========================
    // 2. BROWSER-SPECIFIC NOISE
    // ========================
    "ResizeObserver loop limit exceeded", // Benign browser quirk
    "requestAnimationFrame handler took too long", // Performance warnings
    /Text content does not match server-rendered HTML/, // Next.js hydration mismatch
    "ObjectNotValidError", // Chrome extension errors

    // ========================
    // 3. NETWORK/HTTP ERRORS
    // ========================
    /Network Error/i, // Generic network failures (offline users)
    /timeout of \d+ms exceeded/i, // API request timeouts
    /canceled/i, // Aborted API calls

    // ========================
    // 4. THIRD-PARTY LIBRARIES
    // ========================
    /google.*is not defined/i, // Google Maps/Material errors
    /stripe.*is not defined/i, // Stripe.js loading issues
    /web3/i, // Crypto wallet errors

    // ========================
    // 5. ANGULAR-SPECIFIC
    // ========================
    /ExpressionChangedAfterItHasBeenCheckedError/i, // Dev-mode change detection
    /NG0100:.*changed after check/i, // Angular's new error format
    /ViewDestroyedError/i, // Async operations after component destruction

    // ========================
    // 6. SECURITY/CORS
    // ========================
    /Blocked a frame with origin/i, // iframe security policies
    /Content Security Policy/i,
    /Permission denied to access property/i
  ],

  /**
   * normalizeDepth: Prevent memory overload
   * - Limits how deep Sentry looks into objects
   * - 5 levels is usually enough
   */
  normalizeDepth: 5,

  /**
   * sendDefaultPii: Privacy control
   * - false = Never send personal data (emails, names etc.)
   */
  sendDefaultPii: false
});

if (environment.production && !isLocalhost) {
window.addEventListener('error', function (event) {
  Sentry.captureException(event.error, {
    level: 'error',
    tags: { source: 'window.onerror' },
    contexts: { mechanism: { type: 'onerror', handled: false } }
  });
});

window.addEventListener('unhandledrejection', function (event) {
  Sentry.captureException(event.reason, {
    level: 'error',
    tags: { source: 'unhandledrejection' },
    contexts: { mechanism: { type: 'unhandledrejection', handled: false } }
  });
});
}

platformBrowserDynamic().bootstrapModule(AppModule)
  .catch(err => console.error(err));
